import React, { useEffect, useMemo, useState } from "react";
import { FaDownload, FaPrint } from "react-icons/fa";
import Navbar from "./Navbar";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import { buildCsv, downloadCsvFile } from "../utils/csvDownload";
import {
  buildOfficeDeductionsRows,
  formatDeductionMoney,
  officeDeductionsTotals,
} from "../utils/officeDeductionsReportUtils";
import logo from "../Assets/logo3.jpg";

const COMPANY = "ABC Realty Inc., Brokerage";

const OfficeDeductionsReport = () => {
  const { isAgent, appUser } = useRole();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axiosInstance.get("/trades");
        if (!cancelled) setTrades(res.data || []);
      } catch (err) {
        if (!cancelled) {
          setTrades([]);
          setError(
            err.response?.data?.message || "Could not load trade deductions.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(
    () =>
      buildOfficeDeductionsRows(trades, {
        appUser: isAgent ? appUser : null,
        fromDate,
        toDate,
      }),
    [trades, isAgent, appUser, fromDate, toDate],
  );

  const totals = useMemo(() => officeDeductionsTotals(rows), [rows]);
  const tradeCount = useMemo(
    () => new Set(rows.map((row) => String(row.tradeId || row.tradeNumber))).size,
    [rows],
  );

  const handleDownloadCsv = () => {
    const csv = buildCsv(
      [
        "Trade #",
        "Date",
        "Address",
        "Agent",
        "Deduction",
        "Amount",
        "HST",
        "Total",
      ],
      rows.map((row) => [
        row.tradeNumber,
        row.date,
        row.address,
        row.agentName,
        row.expenseLabel,
        Number(row.amount || 0).toFixed(2),
        Number(row.tax || 0).toFixed(2),
        Number(row.total || 0).toFixed(2),
      ]),
    );
    downloadCsvFile("office-deductions.csv", csv);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="print:hidden">
        <Navbar />
      </div>
      <div className="max-w-screen-2xl mx-auto px-4 py-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="hidden print:block text-center mb-4">
              <img src={logo} alt="" className="h-10 mx-auto mb-2" />
              <p className="font-bold">{COMPANY}</p>
            </div>
            <h1 className="text-2xl font-bold text-blue-900">
              Office Deductions
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {isAgent
                ? "Office expense deductions on your trades."
                : "Office expense deductions on all trades."}
            </p>
          </div>
          <div className="print:hidden flex flex-wrap items-end gap-3">
            <label className="text-sm text-gray-700">
              From
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1 block border border-gray-300 rounded-md px-3 py-2"
              />
            </label>
            <label className="text-sm text-gray-700">
              To
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1 block border border-gray-300 rounded-md px-3 py-2"
              />
            </label>
            <button
              type="button"
              onClick={handlePrint}
              className="bg-green-600 text-white px-4 py-2 rounded flex items-center text-sm"
            >
              <FaPrint className="mr-2" />
              Print
            </button>
            <button
              type="button"
              onClick={handleDownloadCsv}
              className="bg-blue-900 text-white px-4 py-2 rounded flex items-center text-sm"
            >
              <FaDownload className="mr-2" />
              Download CSV
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading deductions...</p>
        ) : error ? (
          <p className="text-sm text-red-700">{error}</p>
        ) : (
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-blue-900 text-white">
                  <th className="px-3 py-2 text-left font-medium">Trade #</th>
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Address</th>
                  <th className="px-3 py-2 text-left font-medium">Agent</th>
                  <th className="px-3 py-2 text-left font-medium">Deduction</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 text-right font-medium">HST</th>
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-3 py-8 text-center text-gray-500"
                    >
                      No office deductions found for this selection.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr
                      key={`${row.tradeId}-${row.agentId}-${row.expenseKey}-${idx}`}
                      className="border-b border-gray-100"
                    >
                      <td className="px-3 py-2">{row.tradeNumber || "—"}</td>
                      <td className="px-3 py-2">{row.date || "—"}</td>
                      <td className="px-3 py-2">{row.address || "—"}</td>
                      <td className="px-3 py-2">{row.agentName || "—"}</td>
                      <td className="px-3 py-2">{row.expenseLabel}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatDeductionMoney(row.amount)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatDeductionMoney(row.tax)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatDeductionMoney(row.total)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-3 py-2" colSpan={5}>
                      Total ({tradeCount} trade{tradeCount === 1 ? "" : "s"},{" "}
                      {rows.length} deduction{rows.length === 1 ? "" : "s"})
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatDeductionMoney(totals.amount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatDeductionMoney(totals.tax)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatDeductionMoney(totals.total)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficeDeductionsReport;
